import React, { ReactNode, useState, useRef, useEffect } from 'react';
import FullScreenIcon from '../../assets/images/full_screen.svg';
import ExitFullScreenIcon from '../../assets/images/exit_full_screen.svg';
import DownloadIcon from '../../assets/images/download_button.svg';
import CloseIcon from '../../assets/images/close_button.svg';
import { useDispatch } from 'react-redux';
import { setTransparentModalOpen } from '../../features/transparentModal/TransparentModal';

interface ModalProps {
    title: string;
    isOpen: boolean;
    onClose: () => void;
    completeUrl: string;
    children: ReactNode;
}

const TransparentModal: React.FC<ModalProps> = ({
    title,
    isOpen,
    onClose,
    completeUrl,
    children,
}) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    useEffect(() => {
        const handleFullscreenChange = () => {
            if (document.fullscreenElement === modalRef.current) {
                setIsFullScreen(true);
            } else {
                setIsFullScreen(false);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange
            );
        };
    }, []);
    useEffect(() => {
        dispatch(setTransparentModalOpen(true));
        return () => {
            dispatch(setTransparentModalOpen(false));
        };
    }, []);
    if (!isOpen) return null;

    const handleFullScreenToggle = () => {
        if (modalRef.current) {
            if (isFullScreen) {
                document.exitFullscreen();
            } else {
                modalRef.current.requestFullscreen();
            }
        }
    };

    const handleDownload = () => {
        if (completeUrl) {
            const a = document.createElement('a');
            a.href = completeUrl;
            a.download = `${title}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } else {
            // Handle case where no PDF URL is provided
            alert('No PDF URL provided!');
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black backdrop-blur-sm bg-opacity-70 h-screen w-screen ">
            <div
                ref={modalRef}
                className={`mt-[2rem] flex flex-col justify-center items-center h-screen w-screen${
                    isFullScreen ? ' ' : 'max-w-screen '
                }`}
            >
                <div className="fixed top-[4%] right-[2%] flex lg:space-x-4 md:space-x-2 z-50">
                    <button
                        data-testid="transparent-modal-download-button"
                        onClick={handleDownload}
                        className="focus:outline-none opacity-100"
                        title="Download"
                    >
                        <img
                            src={DownloadIcon}
                            alt="Download"
                            className="lg:h-8 lg:w-8 md:h-6 md:w-6 sm:h-4 sm:w-4"
                        />
                    </button>
                    <button
                        data-testid="transparent-modal-toggle-full-screen-button"
                        onClick={handleFullScreenToggle}
                        className="focus:outline-none opacity-100"
                        title={
                            isFullScreen ? 'Exit Full Screen' : 'Full Screen'
                        }
                    >
                        <img
                            src={
                                isFullScreen
                                    ? ExitFullScreenIcon
                                    : FullScreenIcon
                            }
                            alt={
                                isFullScreen
                                    ? 'Exit Full Screen'
                                    : 'Full Screen'
                            }
                            className="lg:h-8 lg:w-8 md:h-6 md:w-6 sm:h-4 sm:w-4"
                        />
                    </button>
                    <button
                        data-testid="transparent-modal-close-button"
                        onClick={onClose}
                        className="focus:outline-none opacity-100"
                        title="Close"
                    >
                        <img
                            src={CloseIcon}
                            alt="Close"
                            className="lg:h-10 lg:w-10 md:h-8 md:w-8 sm:h-6 sm:w-6"
                        />
                    </button>
                </div>

                <h1 className="text-white mb-2">{title}</h1>
                <div className=" rounded-[0.625rem] overflow-y-scroll scrollbar-0 mb-2 w-screen ">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default TransparentModal;
